/* Compatibility adapter for Judge.me's carousel and teleported review gallery.
 * Keep vendor rendering/click actions; own only the keyboard and modal contract.
 */
(() => {
  if (window.jcsReviewsInitialized) return;
  window.jcsReviewsInitialized = true;
  const label = document.currentScript?.dataset.openLabel || 'Open review';
  const cardSelector = '.jm-carousel-review-item';
  const gallerySelector = '.jdgm-modal__content[role="dialog"][aria-label="Review Media Gallery"]';
  const focusSelector = 'a[href],button,input,select,textarea,[tabindex],video[controls]';
  const cards = new WeakMap();
  let active = null;
  let opener = null;
  let frame = 0;
  const visible = el => el?.isConnected && el.getClientRects().length > 0 &&
    getComputedStyle(el).visibility !== 'hidden' && !el.closest('[hidden],[aria-hidden="true"]');
  const focusable = el => visible(el) && !el.closest('[inert]') && !el.matches(':disabled,[aria-disabled="true"]');
  const stops = dialog => [...dialog.querySelectorAll(focusSelector)].filter(el => el.tabIndex >= 0 && focusable(el));
  const topModal = () => active && [...document.querySelectorAll('dialog[open]')].some(el =>
    !active.dialog.contains(el) && el.matches(':modal'));
  const focusFirst = () => {
    if (!active || topModal()) return;
    const close = active.dialog.querySelector('.jm-indicator__close');
    (focusable(close) ? close : stops(active.dialog)[0] || active.dialog).focus({preventScroll: true});
  };

  function repairCard(card) {
    let state = cards.get(card);
    if (!state) {
      state = {name: card.getAttribute('aria-label') || label};
      cards.set(card, state);
      // The provider listens on the entire card. Native inner buttons must not
      // also activate its Enter/Space handler before their own click occurs.
      card.addEventListener('keydown', event => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target === card) return;
        event.stopPropagation();
        const thumbnail = event.target.closest('.jm-media-thumbnail-list__thumbnail[role="button"]');
        if (thumbnail) {
          event.preventDefault();
          if (!event.repeat) thumbnail.click();
        }
      }, true);
    }
    const currentName = card.getAttribute('aria-label');
    if (currentName) state.name = currentName;
    card.removeAttribute('role');
    card.removeAttribute('tabindex');
    card.removeAttribute('aria-label');
    let button = card.querySelector('.jm-review-content__expand-button');
    const added = card.querySelector('button.jcs-review-open');
    if (button) {
      if (added && added !== button) added.remove();
      // This carousel opens the full review instead of expanding inline.
      button.removeAttribute('aria-expanded');
    } else {
      button = added;
      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'jcs-review-open';
        (card.querySelector('.jm-carousel-review-item__body') || card).append(button);
      }
    }
    const caption = button.querySelector('.jm-text') || button;
    if (caption.textContent !== label) caption.textContent = label;
    if (button.getAttribute('aria-label') !== state.name) button.setAttribute('aria-label', state.name);
    if (button.getAttribute('aria-haspopup') !== 'dialog') button.setAttribute('aria-haspopup', 'dialog');
    for (const thumb of card.querySelectorAll('.jm-media-thumbnail-list__thumbnail[role="button"]')) {
      if (thumb.tabIndex !== 0) thumb.tabIndex = 0;
      if (thumb.getAttribute('aria-haspopup') !== 'dialog') thumb.setAttribute('aria-haspopup', 'dialog');
    }
  }

  function isolate() {
    // Leave the backdrop live. Native dialogs manage their own top-layer
    // isolation, including the delayed promotion if it opens over the gallery.
    let branch = active.dialog.closest('.jdgm-modal') || active.dialog;
    while (branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling === branch || ['SCRIPT', 'STYLE', 'LINK', 'DIALOG'].includes(sibling.tagName)) continue;
        if (!sibling.inert && !active.inert.has(sibling)) {
          active.inert.add(sibling);
          sibling.inert = true;
        }
      }
      if (branch.parentElement === document.body) break;
      branch = branch.parentElement;
    }
  }

  function finish() {
    const previous = active;
    active = null;
    for (const el of previous.inert) el.inert = false;
    if (previous.tabindex === null) previous.dialog.removeAttribute('tabindex');
    else previous.dialog.setAttribute('tabindex', previous.tabindex);
    let target = previous.opener;
    // The provider can replace cards while the gallery is open.
    if (!focusable(target) && previous.review) {
      const card = [...document.querySelectorAll(cardSelector)].find(el => el.getAttribute('review-uuid') === previous.review);
      target = card?.querySelector('[aria-haspopup="dialog"]');
    }
    if (!focusable(target)) target = document.querySelector('#MainContent');
    if (focusable(target)) target.focus({preventScroll: true});
  }

  function sync() {
    frame = 0;
    document.querySelectorAll(cardSelector).forEach(repairCard);
    const dialog = [...document.querySelectorAll(gallerySelector)].find(visible);
    if (active && active.dialog !== dialog) finish();
    if (dialog && !active) {
      const origin = opener && visible(opener) ? opener : document.activeElement;
      active = {dialog, opener: origin, review: origin?.closest(cardSelector)?.getAttribute('review-uuid'),
        inert: new Set(), tabindex: dialog.getAttribute('tabindex')};
      opener = null;
      dialog.tabIndex = -1;
      isolate();
      focusFirst();
    } else if (active) {
      isolate();
      if (!active.dialog.contains(document.activeElement) || !focusable(document.activeElement)) focusFirst();
    }
  }
  const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };

  document.addEventListener('click', event => {
    if (active) return;
    const card = event.target.closest(cardSelector);
    if (!card) return;
    opener = event.target.closest('button,[role="button"]') || card.querySelector('[aria-haspopup="dialog"]');
    schedule();
  }, true);
  document.addEventListener('keydown', event => {
    if (!active && [...document.querySelectorAll(gallerySelector)].some(visible)) sync();
    if (!active || !visible(active.dialog) || topModal()) return;
    if (event.key === 'Escape') {
      const close = active.dialog.querySelector('.jm-indicator__close');
      if (close) {
        event.preventDefault();
        event.stopImmediatePropagation();
        close.click();
      }
    } else if (event.key === 'Tab') {
      const items = stops(active.dialog);
      const index = items.indexOf(document.activeElement);
      if (!items.length || index === -1 || (event.shiftKey ? index === 0 : index === items.length - 1)) {
        event.preventDefault();
        (event.shiftKey ? items.at(-1) : items[0])?.focus();
        if (!items.length) focusFirst();
      }
    }
  }, true);
  document.addEventListener('focusin', event => {
    if (active && !topModal() && visible(active.dialog) && !active.dialog.contains(event.target)) focusFirst();
  });
  const observer = new MutationObserver(records => {
    if (active || records.some(record => record.target.nodeType === 1 &&
      (record.target.closest('.jdgm-widget,.jdgm-widget-revamp') || [...record.addedNodes].some(node =>
        node.nodeType === 1 && (node.matches('.jdgm-widget,.jdgm-widget-revamp') || node.querySelector('.jdgm-widget,.jdgm-widget-revamp')))))) schedule();
  });
  observer.observe(document.documentElement, {childList: true, subtree: true, attributes: true,
    attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'role', 'tabindex', 'aria-expanded', 'aria-label']});
  sync();
})();
